<div class="customer-settings-page">
  @if (isLoading) {
    <app-loader></app-loader>
  }

  <section class="overview-hero">
    <div class="hero-copy">
      <span class="hero-kicker">Account Settings</span>

      <h1>Manage <span>Your Account</span></h1>

      <p>
        Update your profile, contact preferences, notifications, security, and
        payment methods.
      </p>
    </div>

    <div class="hero-actions">
      <button
        type="button"
        class="overview-btn overview-btn--light"
        (click)="goBack()"
      >
        Back
      </button>

      <button
        type="button"
        class="overview-btn overview-btn--dark"
        (click)="saveChanges()"
      >
        Save Changes
      </button>
    </div>
  </section>

  @if (errorMessage) {
    <div class="inline-alert">
      {{ errorMessage }}
    </div>
  }

  <section class="kpi-grid">
    <article class="kpi-card kpi-card--primary">
      <div>
        <span>Profile Status</span>
        <strong>{{ profileCompletion }}%</strong>
        <p>Profile completion based on standard customer account fields.</p>
      </div>

      <img [src]="assetUrl + 'icons/users-border.svg'" alt="" />
    </article>

    <article class="kpi-card">
      <div>
        <span>Payment Methods</span>
        <strong>{{ cards.length }}</strong>
        <p>{{ primaryCardLabel }}</p>
      </div>

      <img [src]="assetUrl + 'icons/dollar-border.svg'" alt="" />
    </article>

    <article class="kpi-card">
      <div>
        <span>Notifications</span>
        <strong>{{ enabledNotificationCount }}</strong>
        <p>Enabled customer communication preferences.</p>
      </div>

      <img [src]="assetUrl + 'icons/doc-border.svg'" alt="" />
    </article>

    <!-- <article class="kpi-card">
      <div>
        <span>Security</span>
        <strong>{{ securityStatus }}</strong>
        <p>Password update fields are optional unless changing password.</p>
      </div>

      <img [src]="assetUrl + 'icons/review-border.svg'" alt="" />
    </article> -->
  </section>

  <section class="settings-grid">
    <form class="panel" [formGroup]="accountForm" (ngSubmit)="saveChanges()">
      <header class="panel-head">
        <div>
          <span class="panel-kicker">Customer Profile</span>
          <h2>Personal Information</h2>
          <p>Standard details used for bookings and customer communication.</p>
        </div>
      </header>

      <div class="form-grid">
        <label>
          <span>First Name *</span>
          <input
            class="input"
            formControlName="firstName"
            placeholder="Enter first name"
            [class.is-invalid]="!checkValidField('firstName')"
          />
        </label>

        <label>
          <span>Last Name *</span>
          <input
            class="input"
            formControlName="lastName"
            placeholder="Enter last name"
            [class.is-invalid]="!checkValidField('lastName')"
          />
        </label>

        <label>
          <span>Email Address *</span>
          <input
            type="email"
            class="input"
            formControlName="email"
            placeholder="name@example.com"
            [class.is-invalid]="!checkValidField('email')"
          />
        </label>

        <label>
          <span>Phone Number</span>
          <input
            class="input"
            formControlName="phone"
            placeholder="+1 555 555 5555"
          />
        </label>

        <label>
          <span>Birthday</span>
          <input type="date" class="input" formControlName="birthday" />
        </label>

        <label>
          <span>Preferred Contact Method</span>
          <select class="input" formControlName="preferredContactMethod">
            <option value="email">Email</option>
            <option value="sms">SMS</option>
            <option value="phone">Phone</option>
            <option value="none">No preference</option>
          </select>
        </label>

        <label>
          <span>Preferred City</span>
          <input
            class="input"
            formControlName="preferredCity"
            placeholder="Toronto"
          />
        </label>

        <label>
          <span>Preferred Service</span>
          <input
            class="input"
            formControlName="preferredService"
            placeholder="Haircut, nails, facial..."
          />
        </label>

        <label class="full-width">
          <span>Notes</span>
          <textarea
            class="input"
            rows="4"
            formControlName="notes"
            placeholder="Optional preferences, accessibility needs, or booking notes."
          ></textarea>
        </label>
      </div>
    </form>

    <aside class="panel">
      <header class="panel-head panel-head--stacked">
        <div>
          <span class="panel-kicker">Preferences</span>
          <h2>Notifications</h2>
          <p>Choose how you want to receive appointment and promo updates.</p>
        </div>
      </header>

      <div class="toggle-list" [formGroup]="accountForm">
        <label>
          <div>
            <strong>Appointment Reminders</strong>
            <span>Receive reminders before upcoming appointments.</span>
          </div>
          <input type="checkbox" formControlName="appointmentReminders" />
        </label>

        <label>
          <div>
            <strong>Promo Emails</strong>
            <span>Receive promotional codes and seasonal deals.</span>
          </div>
          <input type="checkbox" formControlName="promoEmails" />
        </label>

        <label>
          <div>
            <strong>SMS Updates</strong>
            <span>Receive appointment updates by text message.</span>
          </div>
          <input type="checkbox" formControlName="smsUpdates" />
        </label>

        <label>
          <div>
            <strong>Review Requests</strong>
            <span>Get reminders to review salons after appointments.</span>
          </div>
          <input type="checkbox" formControlName="reviewRequests" />
        </label>
      </div>
    </aside>
  </section>

  <section class="settings-grid">
    <article class="panel">
      <header class="panel-head">
        <div>
          <span class="panel-kicker">Payment & Billing</span>
          <h2>Payment Methods</h2>
          <p>Manage saved cards used for bookings and checkout.</p>
        </div>

        <button
          type="button"
          class="overview-btn overview-btn--dark"
          (click)="AddPayment()"
        >
          Add Payment
        </button>
      </header>

      @if (cards.length === 0) {
        <div class="empty-state">
          <strong>No payment methods added yet</strong>
          <p>Add a payment method to make checkout faster.</p>

          <button
            type="button"
            class="overview-btn overview-btn--dark"
            (click)="AddPayment()"
          >
            Add Payment Method
          </button>
        </div>
      } @else {
        <div class="payment-list">
          @for (card of cards; track card.id) {
            <article
              class="payment-card"
              [class.payment-card--primary]="card.is_primary"
            >
              <div class="payment-main">
                <img
                  [src]="getCardIcon(card.card_number)"
                  alt="Card icon"
                  class="payment-icon"
                />

                <div>
                  <h3>
                    {{ getCardBrand(card.card_number) | titlecase }}
                    ending in {{ card.card_number.slice(-4) }}
                  </h3>

                  <p>Expiry {{ card.expiry_date }}</p>

                  <div class="payment-actions">
                    <button type="button" (click)="editCard(card)">Edit</button>

                    <button
                      type="button"
                      class="danger"
                      (click)="deleteCard(card)"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>

              <div class="payment-status">
                @if (card.is_primary) {
                  <span class="status-pill status-active">Primary</span>
                } @else {
                  <button type="button" (click)="setPrimaryCard(card)">
                    Set Primary
                  </button>
                }
              </div>
            </article>
          }
        </div>
      }
    </article>

    <aside class="panel">
      <header class="panel-head panel-head--stacked">
        <div>
          <span class="panel-kicker">Security</span>
          <h2>Password</h2>
          <p>Leave password fields blank unless you want to change it.</p>
        </div>
      </header>

      <div class="form-grid single" [formGroup]="accountForm">
        <label>
          <span>Current Password</span>
          <input
            type="password"
            class="input"
            formControlName="currentPassword"
            placeholder="Current password"
          />
        </label>

        <label>
          <span>New Password</span>
          <input
            type="password"
            class="input"
            formControlName="newPassword"
            placeholder="Minimum 8 characters"
            [class.is-invalid]="!checkValidField('newPassword')"
          />
        </label>

        <label>
          <span>Confirm Password</span>
          <input
            type="password"
            class="input"
            formControlName="confirmPassword"
            placeholder="Confirm new password"
            [class.is-invalid]="passwordMismatch"
          />
        </label>

        @if (passwordMismatch) {
          <div class="inline-alert compact">
            New password and confirm password do not match.
          </div>
        }
      </div>
    </aside>
  </section>

  <section class="sticky-actions">
    <button
      type="button"
      class="overview-btn overview-btn--light"
      (click)="goBack()"
    >
      Back
    </button>

    <button
      type="button"
      class="overview-btn overview-btn--dark"
      (click)="saveChanges()"
    >
      Save Changes
    </button>
  </section>
</div>
